import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StepHelpers } from '@shared/components/Stepper';
import { Button } from '@shared/ui/button';
import { toast } from '@shared/hooks/use-toast';
import { FormField, FormService } from '@shared/services/FormServices';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { PageHeader } from '@shared/components/PageHeader';
import { DynamicForm } from '@shared/components/DynamicForm';
import { DiscardDialog } from './DiscardDialog';
import { useUtilityServices } from '../../hooks';
import { useCurrency } from '@shared/selectors/globalSelectors';

interface BasicInfoProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export const BasicInfo: React.FC<BasicInfoProps> = ({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating
}) => {
  const dataLoadedRef = useRef(false);
  const [formData, setFormData] = useState<any>({});
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const currency = useCurrency();
  const navigate = useNavigate();

  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
        toast({
          title: "Failed to save progress",
          description: "Your progress may not be saved. Please try again.",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Data loading effect moved below form initialization

  const handleContinue = async() => {
    const isValid = await FormService.validateForm(form);
        if (!isValid) {
          if (setValidationError) {
            setValidationError("Please fill in all required fields.");
          }
          toast({
            title: "Please fill in all required fields.",
            variant: "destructive",
          });
          return;
        }
    if (clearValidationError) {
      clearValidationError();
    }

    const currentValues = form.getValues();

    if (currentValues.endDate && currentValues.effectiveFrom) {
      const start = new Date(currentValues.effectiveFrom);
      const end = new Date(currentValues.endDate);
      
      // Reset time portion to compare dates only if necessary, but standard Date parse is fine
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (end < start) {
        if (setValidationError) {
          setValidationError("End Date must be greater than or equal to Effective From.");
        }
        toast({
          title: "End Date must be on or after Effective From.",
          variant: "destructive",
        });
        return;
      }
    }
    
    const selectedService = utilityServices?.find((s: any) => s.name === currentValues.utilityService);
    console.log("selectedService",selectedService)
    saveStepData({
      ...currentValues,
      unit: `${selectedService?.unit}` || 'NA',
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    });

    if (logTimeUntilStep) {
      logTimeUntilStep("Basic Info");
    }

    if (onNext) onNext();
  };

  const handleDiscard = () => {
    if (stepHelpers && stepHelpers.resetAllData) {
      stepHelpers.resetAllData();
    } else {
      localStorage.removeItem(storageKey);
    }
    navigate("/plan-tarrif/plan-tarrif-landing?tab=tariff");
  };

  const { data: utilityServices } = useUtilityServices();

  const basicInfoFields: FormField[] = useMemo(
      () => [
        {
          name: "rateName",
          label: "Rate Name",
          type: "text",
          placeholder: "e.g. Water - Residential Tiered",
          helperText: "Customer-facing name. Appears on bills, statements, and the consumer portal.",
          classes:FIELD_CLASSES,
          required: true,
        },
        {
          name: "rateCode",
          label: "Rate Code",
          type: "text",
          placeholder: "WTR-RES-T1",
          helperText:
            "Used in billing files and APIs. Format: SERVICE-CATEGORY-VARIANT.",
          classes: FIELD_CLASSES,
          required: true,

        },
        {
          name: "utilityService",
          label: "Utility Service",
          type: "select",
          isSearchable: false,
          placeholder:
            "Select Utility Service...",
          options: utilityServices?.map((u: any) => ({
            label: u.name,
            value: u.name,
          })) || [],
          helperText:
            "The service this tariff bills for. Drives meter type, billing units, and rate structure.",
          classes:FIELD_CLASSES,
          required: true,
        
        },

        {
          name: "effectiveFrom",
          label: "Effective From",
          type: "datepicker",
          placeholder: "Select effective date",
          helperText: "When this tariff begins applying to bills.",
          classes: FIELD_CLASSES,
          required: true,
        },
        {
          name: "endDate",
          label: "Effective To",
          type: "datepicker",
          placeholder: "Select effective date",
          helperText: "Leave blank for open-ended.",
          classes: FIELD_CLASSES,
          
        },
        {
          name: "description",
          label: "Description",
          type: "textarea",
          placeholder: "Internal description and intent of this tariff. Visible to rate analyst and approvers.",
          helperText: "Internal notes for rate analysts and approvers. Plain prose — explain why the tariff exists and any special handling.",
          classes: FIELD_CLASSES,
          className: "md:col-span-2",
        }
      ],
      [utilityServices]
    );

    const form = FormService.createForm({
    fields: basicInfoFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const handleFormSubmit = (data: any) => {

    const isValid = form.trigger();
    if (isValid) {

      if(onNext){
        onNext();
      }
      
    }

    console.log('Form submitted:', data);
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    // console.log('Field changed:', fieldName, value);
  };

  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          setFormData(savedStepData);
          form.reset(savedStepData);
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData, form]);

  return (
    <div className="space-y-6">

    <PageHeader title='Basic Information' subtitle='Provide identifying details for this tariff.'></PageHeader>
        
      <DynamicForm
        fields={basicInfoFields}
        form={form}
        onSubmit={handleFormSubmit}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
        }}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        />

    <div className="flex justify-end gap-3 pt-4">
        <Button
          variant="outline"
          onClick={() => setShowDiscardDialog(true)}
        >
          Cancel
        </Button>
        <Button
          onClick={handleContinue}
          disabled={isValidating}
          className="min-w-[120px]"
        >
          Continue
        </Button>
      </div>

      <DiscardDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog} onDiscard={handleDiscard} />
    </div>
  );
};